import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { Grade, Student, Subject } from '../../types/entities'
import { gradeColor, dayOfWeek, formatDate, gradeTypeLabel } from '../../utils/gradeFormatters'
import { weightedAverage } from '../../utils/gradeMath'

/**
 * Таблица «студент × дата» с цветными бейджами оценок + столбец «Средний» справа.
 * Клик или Enter/цифра по ячейке вызывает onCellClick(studentId, date).
 * Навигация: стрелки — перемещение между ячейками, Enter/цифра — открыть диалог.
 */
export function GradesTable({
  students,
  dates,
  subjects,
  gradesByCell,
  onCellClick,
}: {
  students: Student[]
  dates: string[]
  subjects: Subject[]
  gradesByCell: Map<string, Map<string, Grade[]>>
  onCellClick: (studentId: string, date: string) => void
}) {
  const { t } = useTranslation()
  const tableRef = useRef<HTMLTableElement>(null)

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTableCellElement>,
    rowIdx: number,
    colIdx: number,
    studentId: string,
    date: string,
  ) => {
    const table = tableRef.current
    if (!table) return

    // Цифра → открыть диалог (учитель начинает вводить оценку)
    if (/^[1-9нНпПбБуУоО]$/.test(e.key)) {
      onCellClick(studentId, date)
      return
    }

    if (e.key === 'Enter') {
      onCellClick(studentId, date)
      return
    }

    // Стрелки — переход между ячейками данных (пропуская заголовок и sticky-колонки)
    const rows = table.querySelectorAll<HTMLTableRowElement>('tbody tr')
    const totalRows = rows.length
    const totalCols = dates.length

    let nextRow = rowIdx
    let nextCol = colIdx

    if (e.key === 'ArrowDown' && rowIdx < totalRows - 1) nextRow = rowIdx + 1
    else if (e.key === 'ArrowUp' && rowIdx > 0) nextRow = rowIdx - 1
    else if (e.key === 'ArrowRight' && colIdx < totalCols - 1) nextCol = colIdx + 1
    else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      if (colIdx < totalCols - 1) nextCol = colIdx + 1
      else if (rowIdx < totalRows - 1) { nextRow = rowIdx + 1; nextCol = 0 }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      if (colIdx > 0) nextCol = colIdx - 1
      else if (rowIdx > 0) { nextRow = rowIdx - 1; nextCol = totalCols - 1 }
    } else if (e.key === 'ArrowLeft' && colIdx > 0) nextCol = colIdx - 1
    else return

    if (nextRow !== rowIdx || nextCol !== colIdx) {
      e.preventDefault()
      // +1 к colIdx т.к. первая td в ряду — имя ученика (sticky), индекс данных начинается с 1
      const targetCell = rows[nextRow]?.querySelectorAll('td')[nextCol + 1] as HTMLTableCellElement | null
      targetCell?.focus()
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-auto">
      <table ref={tableRef} className="text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[200px]">
              {t('gradesTable.student')}
            </th>
            {dates.map((d) => (
              <th
                key={d}
                className="px-2 py-2 font-semibold text-gray-700 text-center min-w-[52px]"
                title={formatDate(d)}
              >
                <div className="text-[10px] text-gray-500">{dayOfWeek(d)}</div>
                <div>{d.slice(8, 10)}.{d.slice(5, 7)}</div>
              </th>
            ))}
            <th className="px-3 py-2 font-semibold text-gray-700 text-center sticky right-0 bg-gray-50 z-10 min-w-[80px]">
              {t('gradesTable.average')}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map((s, rowIdx) => (
            <StudentRow
              key={s.id}
              student={s}
              rowIdx={rowIdx}
              dates={dates}
              cellGrades={gradesByCell.get(s.id)}
              subjects={subjects}
              onCellClick={onCellClick}
              onKeyDown={handleKeyDown}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StudentRow({
  student,
  rowIdx,
  dates,
  cellGrades,
  subjects,
  onCellClick,
  onKeyDown,
}: {
  student: Student
  rowIdx: number
  dates: string[]
  cellGrades: Map<string, Grade[]> | undefined
  subjects: Subject[]
  onCellClick: (studentId: string, date: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>, rowIdx: number, colIdx: number, studentId: string, date: string) => void
}) {
  const avg = computeWeightedAverage(cellGrades)
  return (
    <tr className="border-t border-gray-100 hover:bg-primary-soft/20">
      <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap z-10">
        {student.lastName} {student.firstName.charAt(0)}.
      </td>
      {dates.map((d, colIdx) => (
        <GradeCell
          key={d}
          grades={cellGrades?.get(d) ?? []}
          subjects={subjects}
          onClick={() => onCellClick(student.id, d)}
          onKeyDown={(e) => onKeyDown(e, rowIdx, colIdx, student.id, d)}
        />
      ))}
      <td className="px-3 py-2 font-semibold text-center sticky right-0 bg-white border-l border-gray-100">
        {avg != null ? avg.toFixed(2) : <span className="text-gray-300">—</span>}
      </td>
    </tr>
  )
}

function GradeCell({
  grades,
  subjects,
  onClick,
  onKeyDown,
}: {
  grades: Grade[]
  subjects: Subject[]
  onClick: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => void
}) {
  return (
    <td
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className="px-1.5 py-1 text-center cursor-pointer hover:bg-primary-soft/40 border-l border-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
    >
      {grades.length === 0 ? (
        <span className="text-gray-300 text-xs">·</span>
      ) : (
        <div className="flex justify-center gap-0.5 flex-wrap">
          {grades.map((g) => (
            <span
              key={g.id}
              className={`inline-block min-w-[20px] px-1 py-0.5 rounded text-xs font-semibold ${gradeColor(g.value)}`}
              title={tooltip(g, subjects)}
            >
              {g.value}
            </span>
          ))}
        </div>
      )}
    </td>
  )
}

function tooltip(g: Grade, subjects: Subject[]): string {
  const subjectName = subjects.find((s) => s.id === g.subjectId)?.name ?? ''
  const head = `${subjectName} · ${gradeTypeLabel(g.type)}`
  return g.comment ? `${head}\n${g.comment}` : head
}

function computeWeightedAverage(cellGrades: Map<string, Grade[]> | undefined): number | null {
  if (!cellGrades) return null
  return weightedAverage([...cellGrades.values()].flat())
}
